<template>
  <div
    v-if="isVisible"
    class="notification-inline"
    :class="{
      [`notification-inline-${type}`]: true,
      'no-shadow': noShadow,
      filled: filled
    }"
  >
    <SvgIcon
      :name="`components/${iconName}`"
      width="16"
      height="16"
      class="badge-icon"
    />
    <span
      v-if="title"
      class="title"
    >{{ title }}</span>
    <div
      v-else
      class="body"
    >
      <slot />
    </div>

    <a
      v-if="!actionText && !noActions"
      href="#close"
      class="close-icon"
      @click.prevent="close"
    >
      <SvgIcon
        name="components/remove-alt"
        width="16"
        height="16"
      />
    </a>

    <div
      v-else-if="actionText && !noActions"
      class="notification-inline-actions"
    >
      <a
        href="#action"
        @click.prevent="action"
      >{{ actionText }}</a>

      <a
        href="#close"
        @click.prevent="close"
      >Dismiss</a>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    /**
     * Text content to place in the title.
     */
    title: {
      type: String,
      default: null,
    },

    /**
     * Text content to place in the Action Text.
     */
    actionText: {
      type: String,
      default: undefined,
    },

    /**
     * Applies one of the style type to the component.
     */
    type: {
      type: String,
      default: 'info',
      validator: (value) =>
        ['info', 'success', 'warning', 'error'].indexOf(value) !== -1,
    },

    /**
     * When set to 'true', disables the shadow on the component
     */
    noShadow: {
      type: Boolean,
      default: false,
    },

    /**
     * Disables the ability to click the actions of the notification
     */
    noActions: {
      type: Boolean,
      default: false,
    },

    filled: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    isVisible: true,
  }),
  computed: {
    iconName () {
      const icons = {
        info: 'info',
        error: 'error',
        warning: 'warning-alt',
        success: 'check-small',
      }

      return icons[this.type]
    },
  },
  methods: {
    close () {
      this.isVisible = false
    },
    action (e) {
      /**
       * Event triggered by user interaction. Emitted after the action button is clicked
       */
      this.$emit('action', e)
    },
  },
}
</script>

<style lang="scss" scoped>
  .notification-inline {
    display: flex;
    align-items: flex-start;
    border: 1px solid $gray-900;
    min-height: 40px;
    border-radius: 2px;
    padding: 8px 1rem;
    font-size: $font-size-sm;
    background-color: #fff;
    line-height: 1.43;
    font-stretch: normal;
    font-style: normal;
    letter-spacing: normal;

    &:not(.no-shadow) {
      box-shadow: 0 2px 8px 0 rgba(37, 37, 37, 0.16), 0 0 4px 0 rgba(37, 37, 37, 0.08);
    }

    .badge-icon {
      color: $gray-900;
      margin-right: 0.5rem;
      min-width: 16px;
      min-height: 16px;
      margin-top: 4px;
    }

    &-actions {
      a,
      a:hover {
        color: $cyan-700;
      }

      a {
        margin-left: 1rem;
      }
    }

    &-info {
      border-color: $cyan-700;

      .badge-icon {
        color: $cyan-700;
      }

      &.filled {
        background-color: $cyan-100;
      }

      a {
        &,
        &:hover {
          color: $cyan-700;
        }
      }
    }

    &-warning {
      border-color: $orange-700;

      .badge-icon {
        color: $orange-700;
      }

      &.filled {
        background-color: $orange-200;
      }
    }

    &-error {
      border-color: $red-500;

      .badge-icon {
        color: $red-500;
      }

      &.filled {
        background-color: $red-100;
      }
    }

    &-success {
      border-color: $green-700;

      .badge-icon {
        color: $green-700;
      }

      &.filled {
        background-color: $green-200;
      }
    }

    .close-icon {
      display: inline-flex;
      color: $gray-900;
    }

    .title,
    .body {
      flex-grow: 1;
    }
  }
</style>
